import React, { useCallback, useEffect, useState } from "react"
import { gql, useMutation, useQuery } from "@apollo/client"

import Layout from "components/Layout"
import Avatar from "react-avatar"

import PassengersModal from "components/Common/Modals/Passengers"

import useInfiniteScroll from "hooks/useInfiniteScroll"

import { toast } from "react-hot-toast"

import { uri } from "config"

import formatPhoneNumber from "utils/formatPhoneNumber"

const PASSENGERS = gql`
  query Passengers(
    $filter: PassengerFilterInput
    $page: Int
    $size: Int
    $sort: PassengerSortInput
  ) {
    passengers(filter: $filter, page: $page, size: $size, sort: $sort) {
      id
      relationship
      firstName
      lastName
      email
      phone
      gender
      birthdate

      street
      city
      state
      region
      zip
      country

      passportNumber
      passportExparationDate

      # ADDITIONAL
      weight
      height
      size

      emergencyContactPerson
      specialRequest

      roomateRequest
      smoker
      newsletter
    }
  }
`
const CREATE_PASSENGER = gql`
  mutation CreatePassenger($input: CreatePassengerInput!) {
    createPassenger(input: $input) {
      id
      relationship
      firstName
      lastName
      email
      phone
      gender
      birthdate

      street
      city
      state
      region
      zip
      country

      passportNumber
      passportExparationDate

      # ADDITIONAL
      weight
      height
      size

      emergencyContactPerson
      specialRequest

      roomateRequest
      smoker
      newsletter
    }
  }
`
const UPDATE_PASSENGER = gql`
  mutation UpdatePassenger($input: UpdatePassengerInput!) {
    updatePassenger(input: $input) {
      id
      relationship
      firstName
      lastName
      email
      phone
      gender
      birthdate

      street
      city
      state
      region
      zip
      country

      passportNumber
      passportExparationDate

      # ADDITIONAL
      weight
      height
      size

      emergencyContactPerson
      specialRequest

      roomateRequest
      smoker
      newsletter
    }
  }
`
const DELETE_PASSENGER = gql`
  mutation DeletePassenger($id: ID!) {
    deletePassenger(id: $id) {
      id
    }
  }
`

const size = 20

const PassengersPage = () => {
  const [createPassenger] = useMutation(CREATE_PASSENGER)
  const [updatePassenger] = useMutation(UPDATE_PASSENGER)
  const [deletePassenger] = useMutation(DELETE_PASSENGER)

  const { data, loading, error, fetchMore } = useQuery(PASSENGERS, {
    variables: {
      page: 0,
      size: 20,
      sort: { firstName: "ASC" },
    },
  })

  const handleCreate = async (input) => {
    const loadingId = toast.loading("Saving...")
    try {
      const user = await createPassenger({ variables: { input } })
      toast.dismiss(loadingId)
      toast.success("Passenger created!")
      return user
    } catch (error) {
      toast.dismiss(loadingId)
      toast.error("Could not save.")
      throw error
    }
  }

  const handleUpdate = async (input) => {
    const loadingId = toast.loading("Saving...")
    try {
      const user = await updatePassenger({ variables: { input } })
      toast.dismiss(loadingId)
      toast.success("Passenger updated!")
      return user
    } catch (error) {
      toast.dismiss(loadingId)
      toast.error("Could not save.")
      throw error
    }
  }

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this passenger?")) {
      const loadingId = toast.loading("Saving...")
      try {
        const user = await deletePassenger({ variables: { id } })
        toast.dismiss(loadingId)
        toast.success("Passenger updated!")
        return user
      } catch (error) {
        toast.dismiss(loadingId)
        toast.error("Could not save.")
        throw error
      }
    }
  }

  const handleSearch = useCallback(
    (search) => {
      if (!fetchMore) return

      if (search?.trim() === "") {
        fetchMore({
          updateQuery: (
            previousResult,
            { fetchMoreResult, queryVariables },
          ) => {
            return {
              passengers: [...fetchMoreResult.passengers],
            }
          },
        })

        return
      }

      fetchMore({
        variables: { filter: { search } },
        updateQuery: (previousResult, { fetchMoreResult, queryVariables }) => {
          return {
            passengers: [
              // ...previousResult.matches,
              ...fetchMoreResult.passengers,
            ],
          }
        },
      })
    },
    [fetchMore],
  )

  const handleLoadMore = useCallback(
    (page) => {
      if (page === 0) return

      fetchMore({
        variables: { page },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return previousResult
          return Object.assign({}, previousResult, {
            passengers: [
              ...previousResult.passengers,
              ...fetchMoreResult.passengers,
            ],
          })
        },
      })
    },
    [fetchMore],
  )

  const exportPassengers = async () => {
    if (window.confirm(`Are you sure you want to export all passengers?`)) {
      try {
        await toast.promise(
          fetch(`${uri}/reporting/passenger/xlsx/`)
            .then((_) => _.blob())
            .then((blob) => {
              const file = window.URL.createObjectURL(blob)
              window.location.assign(file)
            }),
          {
            loading: "Creating export...",
            success: <b>Export created!</b>,
            error: <b>Could not save.</b>,
          },
        )
      } catch (error) {}
    }
  }

  const [selectedPassenger, setSelectedPassenger] = useState(null)
  const [isModalOpen, setModalOpen] = useState(false)

  const openModal = useCallback(async () => {
    setModalOpen(true)
  }, [setModalOpen])

  const closeModal = useCallback(async () => {
    setModalOpen(false)
    setSelectedPassenger(null)
  }, [setModalOpen])

  const handleSelect = (passenger) => {
    setSelectedPassenger(passenger)
    openModal()
  }

  return (
    <Layout title="Passengers | GTR" isLoading={loading}>
      <BoxHeader
        title="Passengers"
        onExport={exportPassengers}
        onSearch={handleSearch}
        onClick={() => openModal()}
      />

      <PassengersTable
        loading={loading}
        data={data?.passengers}
        onLoadMore={handleLoadMore}
        onSelect={handleSelect}
      />

      <PassengersModal
        mode="single"
        isOpen={isModalOpen}
        onClose={closeModal}
        fields={{
          passportNumber: true,
          passportExparationDate: true,

          weight: true,
          height: true,
          size: true,

          emergencyContactPerson: true,

          specialRequest: false,
          roomateRequest: false,

          smoker: true,
          newsletter: true,

          userId: false,
        }}
        data={selectedPassenger ? selectedPassenger : []}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
      />
    </Layout>
  )
}

export default PassengersPage

const BoxHeader = ({ title, onSearch, onExport, onClick }) => {
  const [query, setQuery] = useState("")

  useEffect(() => {
    onSearch && onSearch(query)
  }, [query])

  return (
    <header className="bg-gray-800 text-gray-100 pb-24">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-12 gap-4">
        <div className="col-span-3 flex items-center">
          <h1 className="font-black text-3xl">{title}</h1>
        </div>

        <div className="col-span-6 relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-4">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 fill-current text-gray-600"
            >
              <path d="M16.32 14.9l1.1 1.1c.4-.02.83.13 1.14.44l3 3a1.5 1.5 0 0 1-2.12 2.12l-3-3a1.5 1.5 0 0 1-.44-1.14l-1.1-1.1a8 8 0 1 1 1.41-1.41l.01-.01zM10 16a6 6 0 1 0 0-12 6 6 0 0 0 0 12z" />
            </svg>
          </div>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search"
            className="block w-full border border-transparent bg-gray-900 focus:outline-none focus:bg-white focus:border-gray-300 text-gray-900 rounded-lg pl-12 pr-4 py-2"
          />
        </div>

        <div className="col-span-3 flex space-x-4 justify-end items-center pl-8">
          <button
            onClick={onExport}
            type="button"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </button>

          <button
            onClick={onClick}
            type="button"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="-ml-1 mr-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Passenger
          </button>
        </div>
      </div>
    </header>
  )
}

const PassengersTable = ({ loading, data, onSelect, onLoadMore }) => {
  const header = ["Name", "Email", "Phone", "Passport"]

  const hasMore = loading ? false : data?.length >= size
  const [page, loaderRef] = useInfiniteScroll({ hasMore })

  useEffect(() => {
    onLoadMore && onLoadMore(page)
  }, [page])

  return (
    <main className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pb-32">
      <div className="relative -mt-16 z-30">
        <div className="bg-white rounded-lg shadow-lg">
          <div className="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200">
            <div style={{ maxHeight: "70vh", overflowY: "scroll" }}>
              <table className="min-w-full table-auto">
                <thead>
                  <tr>
                    {header.map((item, index) => (
                      <th
                        key={index}
                        className="px-6 py-3 border-b border-gray-200 bg-gray-100 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                        style={{ position: "sticky", top: "0" }}
                      >
                        {item}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="table-body">
                  {data?.map((passenger, index) => (
                    <tr
                      key={index}
                      className="table-row"
                      onClick={() => onSelect(passenger)}
                    >
                      <td className="table-cell w-3/12">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <Avatar
                              name={`${passenger?.firstName} ${passenger?.lastName}`}
                              src={passenger?.photoURL}
                              size="40"
                              round={true}
                            />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm leading-5 font-medium text-gray-900">
                              {`${passenger?.firstName} ${passenger?.lastName}`}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell w-3/12">
                        <div className="text-sm leading-5 text-gray-900">
                          {passenger?.email}
                        </div>
                      </td>
                      <td className="table-cell w-3/12">
                        <div className="text-sm leading-5 text-gray-900">
                          {formatPhoneNumber(passenger?.phone)}
                        </div>
                      </td>
                      <td className="table-cell w-3/12">
                        <div className="text-sm leading-5 text-gray-900">
                          {passenger?.passportNumber}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {hasMore && (
                <div ref={loaderRef} className="text-center bg-gray-100">
                  Loading…
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  )
}
